/**
 * 批量事务抽屉 V4.0 - API Hook
 * 封装与后端API的交互逻辑
 *
 * 重构说明：
 * 1. 统一错误处理
 * 2. 改善API调用结构
 * 3. 添加更好的日志记录
 * 4. 使用常量管理API端点
 */

import { useState, useCallback } from 'react';
import api from '../../../../api/enhancedApi';
import { useAuth } from '../../../../hooks/useAuth';
import {
    API_ENDPOINTS,
    ERROR_MESSAGES,
    TRANSACTION_TYPE_TO_SERVICE_TYPE,
    SUPPORTED_TRANSACTION_TYPES
} from '../constants';

/**
 * 统一的错误处理函数
 * @param {Error} error - 错误对象
 * @param {string} operation - 操作名称
 * @returns {string} 格式化的错误消息
 */
const handleAPIError = (error, operation) => {
  console.error(`❌ ${operation} 失败:`, error);

  // 网络错误
  if (!error.response) {
    return ERROR_MESSAGES.NETWORK_ERROR;
  }

  // HTTP错误
  const status = error.response?.status;
  const data = error.response?.data;

  switch (status) {
    case 400:
      return data?.detail || data?.message || '请求参数错误';
    case 401:
      return '登录已过期，请重新登录';
    case 403:
      return '没有权限执行此操作';
    case 404:
      return '请求的资源不存在';
    case 408:
      return ERROR_MESSAGES.TIMEOUT_ERROR;
    case 500:
      return ERROR_MESSAGES.SERVER_ERROR;
    default:
      return data?.detail || data?.message || `${operation}失败，请稍后重试`;
  }
};

/**
 * 批量事务API Hook
 */
export const useBatchTransactionAPI = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  /**
   * 初始化批量事务数据
   * @param {Array|string} flightIds - 航班ID列表
   * @param {string} transactionType - 事务类型
   * @param {number|null} supplierId - 供应商ID
   * @param {Array|null} airports - 机场列表
   * @returns {Promise<Object>} API响应数据
   */
  const initializeBatchTransactionData = useCallback(async (flightIds, transactionType, supplierId = null, airports = null) => {
    const operation = '初始化批量事务数据';
    console.log(`🚀 ${operation}:`, { flightIds, transactionType, supplierId, airports });

    setLoading(true);
    setError(null);

    try {
      // 参数验证
      if (!flightIds || (Array.isArray(flightIds) && flightIds.length === 0)) {
        throw new Error('航班ID不能为空');
      }
      if (!transactionType) {
        throw new Error('事务类型不能为空');
      }

      // 构建请求参数
      const flightIdsStr = Array.isArray(flightIds) ? flightIds.join(',') : flightIds;
      const params = {
        flight_ids: flightIdsStr,
        transaction_type: transactionType
      };

      // 可选参数
      if (supplierId !== null && supplierId !== undefined) {
        params.supplier_id = supplierId;
      }

      if (airports && Array.isArray(airports) && airports.length > 0) {
        params.airports = airports.join(',');
      }

      console.log(`📤 ${operation} 请求参数:`, params);

      // 发送请求
      const response = await api.batchTransactionV4.initializeDataWithSupplier(params);

      console.log(`✅ ${operation} 成功:`, response);
      return response;

    } catch (err) {
      const errorMessage = handleAPIError(err, operation);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 批量处理事务
   * @param {Object} payload - 批量处理请求数据
   * @returns {Promise<Object>} API响应数据
   */
  const batchProcessTransactions = useCallback(async (payload) => {
    const operation = '批量处理事务';
    console.log(`🚀 ${operation}:`, payload);

    setLoading(true);
    setError(null);

    try {
      // 参数验证
      if (!payload) {
        throw new Error('处理数据不能为空');
      }

      // 为payload添加操作者信息和触发源
      const enhancedPayload = {
        ...payload,
        operator: {
          type: "USER",
          id: user?.id || "unknown_user",
          name: user?.username || "unknown_user",
          source: "BATCH_TRANSACTION_DRAWER_V4"
        },
        trigger_source: "BATCH_TRANSACTION_DRAWER_V4",
        // 添加用户上下文信息用于事件日志
        user_context: {
          user_id: user?.id,
          username: user?.username,
          is_superuser: user?.is_superuser || false
        }
      };

      console.log(`📤 ${operation} 请求数据:`, enhancedPayload);

      // 发送请求
      const response = await api.batchTransactionV4.batchProcess(enhancedPayload);

      console.log(`✅ ${operation} 成功:`, response);
      return response;

    } catch (err) {
      const errorMessage = handleAPIError(err, operation);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [user]);

  /**
   * 获取可用的合同项（产品）- 基于激活机场与事务类型
   * @param {string} transactionType - 事务类型
   * @param {Array} airports - 机场列表
   * @returns {Promise<Object>} API响应数据
   */
  const getAvailableProductsByAirports = useCallback(async (transactionType, airports) => {
    const operation = '获取可用产品';
    console.log(`🚀 ${operation}:`, { transactionType, airports });

    setLoading(true);
    setError(null);

    try {
      // 参数验证
      if (!transactionType) {
        throw new Error('事务类型不能为空');
      }

      // 检查事务类型是否支持
      if (!TRANSACTION_TYPE_TO_SERVICE_TYPE[transactionType]) {
        throw new Error(`不支持的事务类型: ${transactionType}`);
      }

      // 将事务类型映射为服务类型（用于V3 API调用）
      const service_type = TRANSACTION_TYPE_TO_SERVICE_TYPE[transactionType];

      const requestParams = {
        service_type,
        airports: airports || []
      };

      console.log(`📤 ${operation} 请求参数:`, requestParams);

      // 发送请求
      const response = await api.batchTransactionV3.getAvailableContractItems(requestParams);

      console.log(`✅ ${operation} 成功:`, response);
      return response;

    } catch (err) {
      const errorMessage = handleAPIError(err, operation);
      setError(errorMessage);
      throw new Error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 清除错误状态
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * 重置所有状态
   */
  const reset = useCallback(() => {
    setLoading(false);
    setError(null);
  }, []);

  return {
    // 状态
    loading,
    error,

    // API方法
    initializeBatchTransactionData,
    batchProcessTransactions,
    getAvailableProductsByAirports,

    // 工具方法
    clearError,
    reset
  };
};
